import { computed, WritableComputedRef } from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'

export type QueryValue = string | number | boolean | number[] | null | undefined
export type QueryPatch = Record<string, QueryValue>

const isEmpty = (value: QueryValue) =>
    value === null || value === undefined || value === false || value === '' || (Array.isArray(value) && !value.length)

export const toQuery = (query: LocationQuery, patch: QueryPatch): LocationQuery => {
    const next: LocationQuery = { ...query }
    for (const [key, value] of Object.entries(patch)) {
        if (isEmpty(value)) delete next[key]
        else if (Array.isArray(value)) next[key] = value.join(',')
        else next[key] = value === true ? '1' : String(value)
    }
    return next
}

export const parseIds = (value: string | null): number[] =>
    value ? Array.from(new Set(value.split(',').map(Number))).filter((id) => Number.isInteger(id) && id > 0) : []

/**
 * Page state kept in the URL, so a link opens the same view. `go` adds a history entry (a step
 * or a tab); `set` replaces the current one (a filter, a sort, a page). Changes made before the
 * router lands the previous one build on it, so two choices in a row both stick.
 */
export default function useRouteQuery() {
    const route = useRoute()
    const router = useRouter()

    const param = (key: string) => {
        const value = route.query[key]
        return typeof value === 'string' && value !== '' ? value : null
    }

    let pending: LocationQuery | null = null

    const navigate = (patch: QueryPatch, replace: boolean) => {
        const query = toQuery(pending ?? route.query, patch)
        pending = query
        const done = () => {
            if (pending === query) pending = null
        }
        return (replace ? router.replace({ query }) : router.push({ query })).finally(done)
    }

    const go = (patch: QueryPatch) => navigate(patch, false)
    const set = (patch: QueryPatch) => navigate(patch, true)

    const text = (key: string): WritableComputedRef<string | null> =>
        computed({ get: () => param(key), set: (value) => void set({ [key]: value }) })

    const id = (key: string): WritableComputedRef<number | null> =>
        computed({ get: () => Number(param(key)) || null, set: (value) => void set({ [key]: value }) })

    const flag = (key: string): WritableComputedRef<boolean> =>
        computed({ get: () => param(key) === '1', set: (value) => void set({ [key]: value }) })

    return { param, go, set, text, id, flag }
}
